import React, { useEffect } from "react";
import { Button, Select, Stack, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import type { TaxOfficeData } from "../../Onboarding/api/taxOfficeApi";
import LabeledInput from "../../Onboarding/components/shared/LabeledInput";

/**
 * Format EIN input to XX-XXXXXXX format (2 digits, dash, 7 digits).
 * Only allows digits and dashes, max 1 dash and 9 digits.
 */
const formatEIN = (value: string): string => {
  // Remove all non-digit characters except dash
  const cleaned = value.replace(/[^\d-]/g, "");

  // Remove all dashes first
  const digitsOnly = cleaned.replace(/-/g, "");

  // Limit to 9 digits
  const limitedDigits = digitsOnly.slice(0, 9);

  // Add dash after 2nd digit if we have more than 2 digits
  if (limitedDigits.length > 2) {
    return `${limitedDigits.slice(0, 2)}-${limitedDigits.slice(2)}`;
  }

  return limitedDigits;
};

interface TaxInfoFormProps {
  initialValues: TaxOfficeData;
  onSubmit: (values: TaxOfficeData) => Promise<boolean>;
  isSubmitting?: boolean;
  submitButtonText?: string;
  showSubmitButton?: boolean;
  onValidationError?: (hasErrors: boolean) => void;
  onFormReady?: (formRef: { submit: () => Promise<boolean> }) => void;
  isOwner?: boolean; // Whether user is business owner (shows business-specific fields)
}

export const TaxInfoForm = ({
  initialValues,
  onSubmit,
  isSubmitting = false,
  submitButtonText = "Submit",
  showSubmitButton = true,
  onValidationError,
  onFormReady,
  isOwner = true, // Default to true for backward compatibility
}: TaxInfoFormProps) => {
  const form = useForm<TaxOfficeData>({
    initialValues,
    validateInputOnBlur: true,
    validateInputOnChange: false,
    validate: {
      ptin: (value: string | undefined) => {
        if (!value || value.trim() === "") return null; // Optional
        if (!/^\d{8}$/.test(value))
          return "PTIN must be 8 digits. Use leading zero if needed.";
        return null;
      },
      efin: (value: string | undefined) => {
        if (!value || value.trim() === "") return null; // Optional
        if (!/^\d{6}$/.test(value)) return "EFIN must be exactly 6 digits";
        return null;
      },
      ein: (value: string | undefined) => {
        // Only required for business owners
        if (isOwner) {
          if (!value || value.trim() === "") return "EIN is required";
        }
        // Validate format if value is provided
        if (value && value.trim() !== "") {
          // Accept format: XX-XXXXXXX (2 digits, dash, 7 digits) or 9 digits without dash
          const digitsOnly = value.replace(/-/g, "");
          if (!/^\d{9}$/.test(digitsOnly))
            return "EIN must be exactly 9 digits";
        }
        return null;
      },
      clientPin: (value: string | undefined) => {
        if (!value || value.trim() === "") return null; // Optional - can be cleared
        if (!/^\d{5}$/.test(value))
          return "Client PIN must be exactly 5 digits";
        return null;
      },
      bankProducts: () => null, // Optional
    },
  });

  // Debounce form values for validation and autosave
  const [debouncedValues] = useDebouncedValue(form.values, 800);

  // Run validation and autosave on debounced values
  useEffect(() => {
    // Only validate if form has been touched
    if (form.isTouched()) {
      const { hasErrors } = form.validate();

      // Autosave if validation passes
      if (!hasErrors && debouncedValues !== initialValues) {
        // Clean empty strings to undefined before submitting
        const cleanedValues = {
          ...debouncedValues,
          ptin: debouncedValues.ptin?.trim() || undefined,
          efin: debouncedValues.efin?.trim() || undefined,
          ein: debouncedValues.ein?.trim() || undefined,
          clientPin: debouncedValues.clientPin?.trim() || undefined,
        };

        onSubmit(cleanedValues)
          .then((success) => {
            if (success) {
              notifications.show({
                title: "Saved",
                message: "Your settings have been saved automatically",
                color: "green",
                autoClose: 2000,
              });
            }
          })
          .catch((error) => {
            console.error("Autosave failed:", error);
          });
      }
    }
  }, [debouncedValues]);

  const handleSubmit = async (values: TaxOfficeData) => {
    const { hasErrors } = form.validate();
    if (hasErrors) {
      notifications.show({
        title: "Validation Error",
        message: "Please fill in all required fields correctly",
        color: "red",
      });
      onValidationError?.(true);
      return false;
    }

    const success = await onSubmit(values);
    onValidationError?.(false);
    return success;
  };

  // Expose submit function to parent component
  React.useEffect(() => {
    if (onFormReady) {
      onFormReady({
        submit: async () => {
          const { hasErrors } = form.validate();
          if (hasErrors) {
            onValidationError?.(true);
            return false;
          }
          return await onSubmit(form.values);
        },
      });
    }
  }, [form, onFormReady, onSubmit, onValidationError]);

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="sm" maw="500px">
        {/* Business-specific fields (owner only) */}
        {isOwner && (
          <>
            <TextInput
              label={
                <LabeledInput
                  label="Employer Identification Number"
                  helperText="An EIN (Employer Identification Number) a unique nine-digit number assigned by the Internal Revenue Service (IRS) to business entities operating in the United States."
                />
              }
              labelProps={{ style: { display: "flex" } }}
              placeholder="XX-XXXXXXX"
              size="sm"
              required
              maxLength={10}
              error={form.errors.ein}
              {...form.getInputProps("ein")}
              onChange={(e) => {
                const formatted = formatEIN(e.currentTarget.value);
                form.setFieldValue("ein", formatted);
              }}
            />

            <TextInput
              label={
                <LabeledInput
                  label="EFIN #"
                  helperText="An EFIN (Electronic Filing Identification Number) is a number issued by the IRS to individuals or firms that have been approved as Authorized IRS e-File providers"
                />
              }
              labelProps={{ style: { display: "flex" } }}
              placeholder="Enter your 6-digit EFIN"
              size="sm"
              error={form.errors.efin}
              {...form.getInputProps("efin")}
            />

            <Select
              label={
                <LabeledInput
                  label="Bank Products"
                  helperText="Bank products are used to process client tax refunds. Select the bank product you'd like to use for processing client tax refunds."
                />
              }
              labelProps={{ style: { display: "flex" } }}
              placeholder="Select a bank product"
              size="sm"
              data={[
                {
                  value: "SBTPG",
                  label: "SBTPG (Santa Barbara Tax Product Group)",
                },
                { value: "Republic", label: "Republic" },
                { value: "3Fund", label: "3Fund" },
                { value: "Refund Advantage", label: "Refund Advantage" },
                { value: "Refundo", label: "Refundo" },
                { value: "EPS", label: "EPS" },
                { value: "Pay Dash", label: "Pay Dash" },
                {
                  value: "N/A",
                  label: "N/A - I don't want to use bank products",
                },
              ]}
              error={form.errors.bankProducts}
              {...form.getInputProps("bankProducts")}
            />

            <TextInput
              label={
                <LabeledInput
                  label="Default Client Signature PIN"
                  helperText="5-digit tax signature PIN is used to electronically sign a tax return when you file it online using tax preparation software. It serves as your electronic signature, replacing the need for a physical signature"
                />
              }
              labelProps={{ style: { display: "flex" } }}
              placeholder="5-digit PIN"
              maxLength={5}
              size="sm"
              error={form.errors.clientPin}
              {...form.getInputProps("clientPin")}
            />
          </>
        )}

        {/* User-specific fields (all users) */}
        <TextInput
          label={
            <LabeledInput
              label="PTIN #"
              helperText="A PTIN (Preparer Tax Identification Number) is a unique number issued by the IRS (e.g., P 1234567) to tax preparers."
            />
          }
          labelProps={{ style: { display: "flex" } }}
          placeholder="00000000"
          size="sm"
          maxLength={8}
          error={form.errors.ptin}
          {...form.getInputProps("ptin")}
        />

        {showSubmitButton && (
          <Button
            type="submit"
            loading={isSubmitting}
            disabled={isSubmitting}
            mt="md"
          >
            {submitButtonText}
          </Button>
        )}
      </Stack>
    </form>
  );
};
